import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Modal, message, Typography, Alert,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getExportJob,
  executeExportJob,
  cancelExportJob,
  type ExportJob,
} from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  pending: 'default',
  exporting: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

export default function ExportJobDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [job, setJob] = useState<ExportJob | null>(null);
  const [loading, setLoading] = useState(true);

  const canWrite = hasPermission('data_management', 'write');

  const loadJob = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getExportJob(id);
      if (res.data?.success && res.data.data) {
        setJob(res.data.data);
      }
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const handleExecute = () => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('data_management.export.execute'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      onOk: async () => {
        try {
          await executeExportJob(id!);
          message.success(t('data_management.export.execute_success'));
          loadJob();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('data_management.export.cancel'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await cancelExportJob(id!);
          message.success(t('data_management.export.cancel_success'));
          loadJob();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading || !job) return null;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/data-management/exports')}>
          {t('app.back')}
        </Button>
        {canWrite && job.status === 'pending' && (
          <Button type="primary" onClick={handleExecute}>
            {t('data_management.export.execute')}
          </Button>
        )}
        {canWrite && job.status === 'exporting' && (
          <Button danger onClick={handleCancel}>
            {t('data_management.export.cancel')}
          </Button>
        )}
      </Space>

      <Title level={4}>{job.name}</Title>

      {job.error_message && (
        <Alert type="error" message={job.error_message} style={{ marginBottom: 16 }} showIcon />
      )}

      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('data_management.export.field.name')}>{job.name}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.status')}>
          <Tag color={statusColors[job.status] ?? 'default'}>
            {t(`data_management.export.statuses.${job.status}`, job.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.description')} span={2}>
          {job.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.source_module')}>
          {t(`data_management.import.modules.${job.source_module}`, job.source_module)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.file_format')}>
          {t(`data_management.import.formats.${job.file_format}`, job.file_format)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.total_rows')}>
          {job.total_rows > 0 ? job.total_rows.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.exported_rows')}>
          {job.exported_rows > 0 ? job.exported_rows.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.columns')}>
          {job.columns?.length > 0 ? job.columns.join(', ') : t('common.all')}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.include_headers')}>
          <Tag color={job.include_headers ? 'green' : 'default'}>
            {job.include_headers ? t('app.yes') : t('app.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.sort_field')}>
          {job.sort_field || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.sort_descending')}>
          {job.sort_descending ? t('app.yes') : t('app.no')}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.file_name')}>
          {job.file_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.file_size')}>
          {job.file_size > 0 ? `${(job.file_size / 1024).toFixed(1)} KB` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.owner')}>{job.owner_id}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.expires_at')}>
          {job.expires_at ? new Date(job.expires_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.started_at')}>
          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.export.field.completed_at')}>
          {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
